import { Link } from 'react-router-dom';
import { ArrowRight, BookOpen } from 'lucide-react';

const pages = [
  { number: 11, title: 'Module 2 Title Page', type: 'content' },
  { number: 12, title: 'Core Concept & Tool: Evidence-Quality Scale', type: 'content' },
  { number: 13, title: 'Activity: Identify & Label Assumptions', type: 'activity', route: '/elevation/workbook/oqf/module-2/worksheet-2-1' },
  { number: 14, title: 'Module 2 Notes', type: 'content' }
];

export default function Module2Page() {
  return (
    <div className="min-h-screen bg-[var(--bg-raised)]">
      <header className="bg-[var(--slate-dim)] border-b border-[var(--slate)]">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <Link to="/elevation/workbook/oqf" className="text-[var(--text-sec)] hover:text-[var(--text-pri)] text-sm mb-4 inline-block">
            ← Back to OQF Home
          </Link>
          <h1 className="text-3xl font-bold text-[var(--text-pri)]">
            Module 2: Surface & Label Assumptions
          </h1>
          <p className="text-[var(--text-sec)] mt-2">
            Objective: By the end of this module, you will be able to identify hidden assumptions in a project and label their evidence quality.
          </p>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="bg-[var(--slate-dim)] border border-[var(--slate)] p-6 mb-8">
          <h2 className="text-xl font-semibold text-[var(--text-pri)] mb-4">
            💡 Concept: The "Clarity Ladder"
          </h2>
          <p className="text-[var(--text-sec)] mb-4">
            The OQF helps teams see the difference between belief and evidence. Most teams run on "Anecdotes." High-performing teams know how to move up the ladder.
          </p>
          <p className="text-[var(--text-sec)]">
            The goal isn't to have "Verified" evidence for everything (that's too slow). The goal is to <strong>know which rung of the ladder you are standing on</strong> before you make a high-stakes decision.
          </p>
        </div>

        <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-6 mb-8">
          <h2 className="text-xl font-semibold text-[var(--text-pri)] mb-4">
            🛠️ Tool: The Evidence-Quality Scale
          </h2>
          <p className="text-[var(--text-sec)] mb-4">
            Use this shared language to label your team's confidence.
          </p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-[var(--surface)]">
                  <th className="px-4 py-3 text-left font-semibold text-[var(--text-pri)]">Level</th>
                  <th className="px-4 py-3 text-left font-semibold text-[var(--text-pri)]">Label</th>
                  <th className="px-4 py-3 text-left font-semibold text-[var(--text-pri)]">What It Means</th>
                  <th className="px-4 py-3 text-left font-semibold text-[var(--text-pri)]">Typical Source</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--rule)]">
                <tr>
                  <td className="px-4 py-3 font-bold text-lg">1️⃣</td>
                  <td className="px-4 py-3 font-medium text-[var(--text-pri)]">Anecdote</td>
                  <td className="px-4 py-3 text-[var(--text-sec)]">"We've heard..." Based on individual impressions, stories, or intuition.</td>
                  <td className="px-4 py-3 text-[var(--text-sec)]">Personal opinions, isolated customer comments.</td>
                </tr>
                <tr className="bg-[var(--surface)]">
                  <td className="px-4 py-3 font-bold text-lg">2️⃣</td>
                  <td className="px-4 py-3 font-medium text-[var(--text-pri)]">Directional</td>
                  <td className="px-4 py-3 text-[var(--text-sec)]">"Early signals point to..." Some structured observation or small-sample data supports it.</td>
                  <td className="px-4 py-3 text-[var(--text-sec)]">Limited experiments, early interviews.</td>
                </tr>
                <tr>
                  <td className="px-4 py-3 font-bold text-lg">3️⃣</td>
                  <td className="px-4 py-3 font-medium text-[var(--text-pri)]">Strong</td>
                  <td className="px-4 py-3 text-[var(--text-sec)]">"Multiple data sources agree..." Several consistent data points or independent confirmations exist.</td>
                  <td className="px-4 py-3 text-[var(--text-sec)]">Quantitative metrics, trend analysis.</td>
                </tr>
                <tr className="bg-[var(--surface)]">
                  <td className="px-4 py-3 font-bold text-lg">4️⃣</td>
                  <td className="px-4 py-3 font-medium text-[var(--text-pri)]">Verified</td>
                  <td className="px-4 py-3 text-[var(--text-sec)]">"We've checked, and it holds." High-confidence fact confirmed through rigorous, recent validation.</td>
                  <td className="px-4 py-3 text-[var(--text-sec)]">Audited data, controlled experiments.</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div className="mb-8">
          <h2 className="text-2xl font-bold text-[var(--text-pri)] mb-6">Module Pages</h2>
          <div className="space-y-2">
            {pages.map((page) => (
              <div key={page.number} className="flex items-center justify-between p-4 bg-[var(--bg-raised)] border border-[var(--rule)] hover:border-[var(--slate)] transition-colors">
                <div className="flex items-center gap-4">
                  <div className="flex items-center justify-center w-8 h-8 bg-[var(--slate-dim)] text-[var(--text-sec)] rounded-full font-semibold text-sm">
                    {page.number}
                  </div>
                  <div>
                    <h3 className="font-semibold text-[var(--text-pri)]">{page.title}</h3>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {page.type === 'activity' ? (
                    <Link
                      to={page.route!}
                      className="text-[var(--text-ter)] hover:text-[var(--text-ter)] font-medium flex items-center gap-1"
                    >
                      Open Activity
                      <ArrowRight className="w-4 h-4" />
                    </Link>
                  ) : (
                    <BookOpen className="w-5 h-5 text-[var(--text-ter)]" />
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="flex gap-4">
          <Link to="/elevation/workbook/oqf/module-1" className="px-4 py-2 border border-[var(--rule)] text-[var(--text-sec)] hover:bg-[var(--surface)]">
            ← Previous Module
          </Link>
          <Link to="/elevation/workbook/oqf/module-2/worksheet-2-1" className="px-4 py-2 bg-[var(--slate)] text-white hover:bg-[var(--slate)] flex items-center gap-2">
            Start Activity
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </div>
  );
}






